import CarouselRail from "@/components/yts/CarouselRail";
import ExperienceCard from "@/components/yts/ExperienceCard";
import ServiceTypeTile from "@/components/yts/ServiceTypeTile";
import { Section } from "@/components/yts/HomeSections";
import { useExplore, toCard, photosFirst } from "@/lib/ExploreContext";
import { SERVICES_CATEGORY, subcategoriesFor, subMatches } from "@/data/taxonomy";
import { useLang } from "@/lib/i18n";
import { categoryLabel } from "@/lib/locales";

/**
 * The same editorial carousel system for Services:
 * real businesses first, then subcategories with multiple matches,
 * then an accessible horizontal rail of still-unclaimed service types.
 * Every public service remains reachable without page-long card stacks.
 */
export default function ServicesCategoryView({ listings = [], loading, clearFilters }) {
  const { t, lang } = useLang();
  const { nbNames, filterSubs, setFilterSubs } = useExplore();
  const es = lang === "es";
  const live = photosFirst(listings.filter((item) => item?.id && item?.name && !item.coming_soon));
  const pending = listings.filter((item) => item?.id && item.coming_soon);
  const chosenSubs = [...filterSubs];

  const open = subcategoriesFor(SERVICES_CATEGORY)
    .map((sub) => pending.find((item) => item.subcategory === sub && !live.some((x) => subMatches(x, sub))))
    .filter(Boolean);
  const groups = subcategoriesFor(SERVICES_CATEGORY)
    .filter((sub) => !chosenSubs.length || chosenSubs.includes(sub))
    .map((sub) => ({sub, items:live.filter((item) => subMatches(item,sub))}))
    .filter((group) => group.items.length >= 2);
  const found = new Set(groups.flatMap((g) => g.items.map((x) => x.id)));
  const other = live.filter((item) => !found.has(item.id));
  const rails = [
    ...groups.map((group) => ({ ...group, title: categoryLabel(group.sub, lang) })),
    ...(other.length ? [{sub:"more", title: es ? "Más servicios locales" : "More local services", items:other}] : []),
  ];

  return (
    <div className="pb-10">
      <Section id="category-results" className="scroll-mt-[164px]">
        <div className="border-t-2 border-[#171719] pt-4 dark:border-white">
          <p className="text-[10px] font-bold uppercase tracking-[.12em] text-[#a53b37] dark:text-[#ffaaa0]">
            {es ? "YA TÚ SABES PR / SERVICIOS" : "YA TÚ SABES PR / SERVICES"}
          </p>
          <h1 className="mt-2 text-[clamp(28px,6vw,50px)] font-extrabold tracking-[-.035em]">
            {es ? "Servicios locales." : "Local services."}
          </h1>
          <p className="mt-1 max-w-[55ch] text-[13px] leading-relaxed text-[#77736d] dark:text-white/65">
            {es
              ? "Encuentra negocios y servicios, o filtra por tipo y área."
              : "Find local businesses and practical services, or narrow by type and area."}
          </p>
        </div>
      </Section>
      {loading ? (
        <Section><p role="status" className="py-6 text-[13px]">{es ? "Cargando servicios…" : "Loading services…"}</p></Section>
      ) : (
        <>
          {rails.map((rail) => (
            <CarouselRail key={rail.sub}
              title={rail.title}
              subtitle={es ? `${rail.items.length} opciones locales` : `${rail.items.length} local options`}
              itemWidth="w-[min(70vw,252px)] min-w-[190px] sm:w-[235px] sm:min-w-0">
              {rail.items.map((listing) => (
                <ExperienceCard key={listing.id} item={toCard(listing,nbNames)} />
              ))}
            </CarouselRail>
          ))}
          {!rails.length && !open.length && (
            <Section>
              <div className="py-10 text-center">
                <p className="text-[14px] font-semibold">{t("services.noMatch")}</p>
                <button type="button" onClick={clearFilters}
                  className="mt-3 min-h-10 rounded-md border border-black/20 px-4 text-[12px] font-bold dark:border-white/30">
                  {t("results.clear")}
                </button>
              </div>
            </Section>
          )}
          {open.length > 0 && (
            <CarouselRail
              title={es ? "Próximamente en la guía" : "Coming to the guide"}
              subtitle={es ? "Tipos de servicios que estamos incorporando." : "More local service categories we're working to add."}
              itemWidth="w-[min(72vw,245px)] min-w-[185px] sm:w-[225px] sm:min-w-0">
              {open.map((item) => <ServiceTypeTile key={item.id} listing={item}/>)}
            </CarouselRail>
          )}
        </>
      )}
    </div>
  );
}
